import { CalendarEvent } from '@/shared/types';
import { supabaseService } from '@/shared/services/supabaseService';
import { authService } from '@/shared/services/realData/auth';

/**
 * Calendar operations
 * Handles all calendar event-related database operations
 */
export const calendarService = {
  /**
   * Get all calendar events for the current user
   */
  async getEvents(): Promise<CalendarEvent[]> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    const { data, error } = await supabaseService.client
      .from('calendar_events')
      .select('*')
      .eq('user_id', supabaseUserId);

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a new calendar event
   */
  async createEvent(eventData: Partial<CalendarEvent>): Promise<CalendarEvent> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    const newEvent = {
      ...eventData,
      id: crypto.randomUUID(),
      userId: supabaseUserId, // Keep for backward compatibility
      user_id: supabaseUserId, // Use for Supabase
      createdAt: new Date().toISOString()
    } as CalendarEvent;

    const { error } = await supabaseService.client
      .from('calendar_events')
      .insert(newEvent);

    if (error) throw error;
    return newEvent;
  },

  /**
   * Update an existing calendar event
   */
  async updateEvent(eventData: CalendarEvent): Promise<void> {
    const { error } = await supabaseService.client
      .from('calendar_events')
      .update(eventData)
      .eq('id', eventData.id);

    if (error) throw error;
  },

  /**
   * Delete a calendar event
   */
  async deleteEvent(eventId: string): Promise<void> {
    const { error } = await supabaseService.client
      .from('calendar_events')
      .delete()
      .eq('id', eventId);

    if (error) throw error;
  }
};
